// Legal Contracts dashboard.
//
// Chrome mirrors the Bunker Operations dashboard
// (``frontend/src/components/TradeOperationsLayout.js``):
//  - ``<Layout>`` shell with the left side nav
//  - Blue gradient ``.legal-screen`` background
//  - Shared ``<HMSPageHeader>`` band: brand lockup, the contract-type pill
//    tray, and ``<TimeNavigationBar>`` riding in its actions slot (same
//    component, same wiring as Trade Ops)
//  - Global ``<Footer />`` at the bottom
//
// Bunkers, Cargo (contracts created on a solids recap leg), Freight and Other
// (contracts added here with Add contract, tied to no deal) tabs.
// A tab may still be switched off with ``enabled: false``; it then renders
// dimmed and cannot be picked.

import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract,
  faMountain,
  faPlus,
  faPenToSquare,
  faShip,
  faTint,
} from '@fortawesome/free-solid-svg-icons';

import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSButton from '../../components/ui/HMSButton';
import TimeNavigationBar from '../../components/TimeNavigationBar';
import AddContractModal from '../../components/Legal/AddContractModal';
import ContractsList from './components/ContractsList';

import { tradeOpsYearRange } from '../../utils/tradeOpsDateRange';

import './LegalDashboard.css';

const TABS = [
  {
    key: 'bunkers',
    label: 'Bunkers Contracts',
    icon: faTint,
    enabled: true,
    listParams: { commodity_type: 'fuels', is_active: true },
  },
  {
    key: 'cargo',
    label: 'Cargo Contracts',
    icon: faMountain,
    enabled: true,
    listParams: { commodity_type: 'solids' },
  },
  {
    key: 'freight',
    label: 'Freight Contracts',
    icon: faShip,
    enabled: true,
    listParams: { commodity_type: 'freight' },
  },
  {
    key: 'drafts',
    label: 'Drafts / Inactive',
    icon: faPenToSquare,
    enabled: true,
    listParams: { commodity_type: 'fuels', is_active: false },
  },
  {
    key: 'other',
    label: 'Other Contracts',
    icon: faFileContract,
    enabled: true,
    listParams: { commodity_type: 'general' },
  },
];

const LegalDashboard = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeTabKey, setActiveTabKey] = useState('bunkers');

  // Year navigator state — mirrors TradeOperationsLayout exactly so the
  // affordance behaves identically across modules. ``selectedDate`` is
  // restored from ``location.state`` whenever the trader navigates back
  // from a contract detail screen so they don't lose their place.
  const restoredDate = location.state?.selectedDate
    ? new Date(location.state.selectedDate)
    : new Date();
  const [selectedDate, setSelectedDate] = useState(restoredDate);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // ``refreshKey`` is a monotonically-increasing counter that
  // ``ContractsList`` watches so the refresh button forces a re-fetch
  // even when the selected year hasn't changed. Same pattern Trade Ops
  // uses (it calls ``fetchData(selectedDate)`` directly; we trigger
  // a child re-fetch through the counter).
  const [refreshKey, setRefreshKey] = useState(0);
  // Add contract (Chris, 2026-10-19). Everyone who opens Legal may add one
  // (management and Legal Approvers); the server re-checks.
  const [adding, setAdding] = useState(false);

  const handleRefresh = useCallback(() => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setRefreshKey((k) => k + 1);
    // Same 800ms minimum spin duration TradeOperationsLayout uses so the
    // icon doesn't blip too fast to read.
    setTimeout(() => setIsRefreshing(false), 800);
  }, [isRefreshing]);

  const activeTab = TABS.find((t) => t.key === activeTabKey) || TABS[0];

  // Pill tray for the shared header — no counts (the old tab strip showed
  // none either) and no per-tab tint, so the tiles keep the same mono brand
  // colour the shared tab system gave them. The pill tray ships no disabled
  // variant, so a tab not yet enabled (Cargo) carries the dim treatment on
  // its className and the change guard below keeps it unselectable.
  const legalTabs = TABS.map((tab) => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon,
    className: tab.enabled ? undefined : 'legal-pill-disabled',
  }));

  const handleTabChange = (key) => {
    const tab = TABS.find((t) => t.key === key);
    if (tab && tab.enabled) setActiveTabKey(key);
  };

  // ISO start/end of the selected year, so the list filters to contracts
  // created within it. This used to be computed inline here — a third copy of
  // the same maths, after Trade Ops and the offline pack — and copies of a
  // date calculation are how the offline snapshot came to be stored under a
  // key no screen asks for. One helper, every caller.
  const { startDate, endDate } = tradeOpsYearRange(selectedDate);

  const listParamsWithYear = {
    ...(activeTab.listParams || {}),
    start_date: startDate,
    end_date: endDate,
  };

  return (
    <Layout showLeftNavSidebar showRightSidebar={false}>
      <div className="legal-screen">
        {/* Shared screen header: brand lockup, contract-type pill tray and
            the year selector in the actions slot. */}
        <HMSPageHeader
          title="Contracts"
          kicker="Legal"
          tabs={{ items: legalTabs, activeKey: activeTabKey, onChange: handleTabChange }}
          actions={
            <div className="legal-header-actions">
              <HMSButton variant="primary" onClick={() => setAdding(true)}>
                <FontAwesomeIcon icon={faPlus} /> Add contract
              </HMSButton>
              <TimeNavigationBar
                onDateChange={setSelectedDate}
                initialDate={selectedDate}
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing}
              />
            </div>
          }
        />

        <div className="legal-tab-content">
          {activeTab.enabled && (
            <ContractsList
              listParams={listParamsWithYear}
              refreshKey={refreshKey}
              onRowClick={(contract) =>
                history.push({
                  pathname: `/dashboard/legal/contracts/${contract.id}`,
                  // Persist the selected year so the back navigation
                  // from the contract detail restores the trader's
                  // place — same pattern Trade Ops uses.
                  state: { selectedDate: selectedDate.toISOString() },
                })
              }
            />
          )}
        </div>

        <AddContractModal
          isOpen={adding}
          onClose={() => setAdding(false)}
          onAdded={(contract) => {
            setAdding(false);
            history.push({
              pathname: `/dashboard/legal/contracts/${contract.id}`,
              state: { selectedDate: selectedDate.toISOString(), added: true },
            });
          }}
        />

        <Footer />
      </div>
    </Layout>
  );
};

export default LegalDashboard;
